import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {GatewayResponse} from '../../../core/auth/api/auth-api.service';
import {InboundConfiguration, Mailbox, MailboxDTO, OutboundConfiguration} from '../mailbox.model';
import {unwrapGateway} from '../../../shared/utils/gateway.utils';
import {map} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MailboxConfiguratorService {
	private http = inject(HttpClient);

	// Mailboxes
	getMailboxes() {
		return this.http.get<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/auth/api/v1/mailboxes`
		).pipe( unwrapGateway<Mailbox>() )
	}

	getMailbox(mailboxId: string) {
		return this.http.get<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/auth/api/v1/mailboxes/${mailboxId}`
		).pipe(
			unwrapGateway<Mailbox>(),
			map(data => data[0])
		)
	}

	addMailbox(body: MailboxDTO) {
		return this.http.post<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/auth/api/v1/mailboxes`,
				body,
		).pipe(
			unwrapGateway<Mailbox>(),
			map(data => data[0])
		)
	}

	updateMailbox(body: MailboxDTO, mailboxId: string) {
		return this.http.post<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/auth/api/v1/mailboxes/${mailboxId}`,
			body,
		).pipe(
			unwrapGateway<Mailbox>(),
			map(data => data[0])
		)
	}

	removeMailbox(mailboxId: string) {
		return this.http.delete<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/auth/api/v1/mailboxes/${mailboxId}`
		).pipe(
			unwrapGateway<Mailbox>(),
			map(data => data[0])
		)
	}

	// Configurations
	getConfigurationsById(mailboxId: string) {
		return this.http.get<GatewayResponse<Mailbox>>(
			`${environment.api.baseUrl}/mailmanager/api/v1/mailboxes/${mailboxId}`
		).pipe(
			unwrapGateway<Mailbox>(),
			map( (mailboxes: Mailbox[]) => mailboxes[0])
		)
	}

	createInbound(body: any) {
		return this.http.post<GatewayResponse<InboundConfiguration>>(
			`${environment.api.baseUrl}/mailmanager/api/v1/email/configuration/inbound`,
			body
		).pipe(
			unwrapGateway<InboundConfiguration>(),
			map((configs: InboundConfiguration[]) => configs[0])
		);
	}

	updateInbound(id: string, body: any) {
		return this.http.put<GatewayResponse<InboundConfiguration>>(
			`${environment.api.baseUrl}/mailmanager/api/v1/email/configuration/inbound/${id}`,
			body
		).pipe(
			unwrapGateway<InboundConfiguration>(),
			map((configs: InboundConfiguration[]) => configs[0])
		);
	}

	createOutbound(body: any) {
		return this.http.post<OutboundConfiguration>(
			`${environment.api.baseUrl}/mailmanager/api/v1/configuration/outbound`,
			body
		);
	}

	updateOutbound(id: string, body: any) {
		return this.http.put<OutboundConfiguration>(
			`${environment.api.baseUrl}/mailmanager/api/v1/configuration/outbound/${id}`,
			body
		);
	}

	updateMailboxInbound(mailboxId: string, configId: string) {
		return this.http.put(
			`${environment.api.baseUrl}/mailmanager/api/v1/mailboxes/${mailboxId}/inbound-configuration/${configId}`,
			{}
		);
	}

	updateMailboxOutbound(mailboxId: string, configId: string) {
		return this.http.put(
			`${environment.api.baseUrl}/mailmanager/api/v1/mailboxes/${mailboxId}/outbound-configuration/${configId}`,
			{}
		);
	}

	startListener(body: any) {
		return this.http.post<any>(
			`${environment.api.baseUrl}/mailmanager/api/v1/email/inbound/listener/start`,
			body
		);
	}
}
